import { Box, Flex, Text } from '@chakra-ui/react';
import { LuCheck } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { FALLBACK_GOAL_COLOR } from '../../utils/goals';

// Past this many months, one dot per month stops being readable and the bar
// says the same thing in less space.
const MAX_DOTS = 12;

export function GoalBar({ pct, color, h = '6px' }) {
  const width = Math.min(100, Math.max(0, pct || 0));
  return (
    <Box h={h} borderRadius="full" bg="redesign.bg.card" overflow="hidden" w="100%">
      <Box h="100%" borderRadius="full" bg={color || FALLBACK_GOAL_COLOR} w={`${width}%`} />
    </Box>
  );
}

function MonthDots({ done, total }) {
  return (
    <Flex align="center" gap="3px">
      {Array.from({ length: total }, (_, i) => (
        <Box
          key={i}
          w="8px"
          h="8px"
          borderRadius="full"
          border="1.5px solid"
          borderColor={i < done ? 'redesign.accent.green' : 'redesign.border'}
          bg={i < done ? 'redesign.accent.green' : 'transparent'}
          flexShrink={0}
        />
      ))}
    </Flex>
  );
}

/**
 * The compact progress shown on a goal's row: a bar for a goal saved toward a
 * date, one dot per month for a goal saved by a monthly amount, and a pill once
 * the goal is fully funded.
 */
export default function GoalProgress({ goal, summary }) {
  const { t } = useTranslation('goals');
  if (!summary) return null;

  if (summary.is_achieved) {
    return (
      <Flex
        display="inline-flex"
        align="center"
        gap="6px"
        h="21px"
        px="9px"
        borderRadius="full"
        bg="rgba(21, 128, 61, 0.10)"
        _dark={{ bg: 'rgba(74, 222, 128, 0.14)' }}
        color="redesign.amount.income"
      >
        <LuCheck size={12} />
        <Text fontSize="2xs" fontWeight={500} whiteSpace="nowrap">{t('status.achieved')}</Text>
      </Flex>
    );
  }

  if (goal.saving_mode === 'monthly_contribution' && goal.monthly_contribution > 0) {
    const total = Math.ceil(goal.target_amount / goal.monthly_contribution);
    if (total <= MAX_DOTS) {
      const done = Math.min(total, Math.max(0, Math.floor(summary.saved / goal.monthly_contribution)));
      return <MonthDots done={done} total={total} />;
    }
  }

  return <GoalBar pct={summary.pct} color={goal.color} />;
}
